// Charts for Home (design round 4): plain SVG, no library. Amounts arrive as decimal strings and
// are converted to whole cents only to place shapes; every label shows the exact figure.
import { useState } from 'react';
import { dollars } from './format.js';

export interface TrendMonth {
  month: string;
  income: string;
  spending: string;
  profit: string;
  cash: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthName = (iso: string) => MONTHS[Number(iso.slice(5, 7)) - 1] ?? '';
const monthYear = (iso: string) => `${monthName(iso)} ${iso.slice(0, 4)}`;

/** Whole cents as a number, for drawing only. */
function cents(decimal: string): number {
  const negative = decimal.startsWith('-');
  const [whole = '0', frac = ''] = decimal.replace('-', '').split('.');
  const v = Number(whole) * 100 + Number(frac.padEnd(2, '0').slice(0, 2));
  return negative ? -v : v;
}

/** Round axis ticks from zero: steps of 1, 2, 2.5 or 5 times a power of ten, in cents. */
function niceTicks(maxCents: number, count = 4): number[] {
  const target = Math.max(maxCents, 10000) / count;
  const pow = 10 ** Math.floor(Math.log10(target));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * pow >= target) ?? 10) * pow;
  const top = Math.ceil(Math.max(maxCents, 1) / step) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= top + step / 2; t += step) ticks.push(t);
  return ticks;
}

/** Short axis label: $800, $1.2k, $15k. */
function short(c: number): string {
  const d = Math.abs(c) / 100;
  const sign = c < 0 ? '-' : '';
  if (d >= 1000) return `${sign}$${(d / 1000).toFixed(d >= 10000 ? 0 : 1).replace(/\.0$/, '')}k`;
  return `${sign}$${Math.round(d)}`;
}

const W = 720;
const H = 240;
const PAD = { top: 16, right: 12, bottom: 28, left: 52 };

/** Income and spending side by side for each month; hover or tap a month for its figures. */
export function MonthlyChart({ months }: { months: TrendMonth[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const ticks = niceTicks(Math.max(...months.flatMap((m) => [cents(m.income), cents(m.spending)])));
  const top = ticks.at(-1) ?? 1;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const slot = innerW / months.length;
  const bar = Math.min(16, slot / 3);
  const y = (c: number) => PAD.top + innerH - (Math.max(c, 0) / top) * innerH;
  const focus = hover ?? months.length - 1;
  const m = months[focus];
  const summary = months
    .map(
      (x) => `${monthYear(x.month)}: income ${dollars(x.income)}, spending ${dollars(x.spending)}`,
    )
    .join('; ');
  return (
    <figure className="chart">
      <div className="chart-head">
        <figcaption>Income and spending</figcaption>
        <div className="legend">
          <span className="key in" /> Income <span className="key out" /> Spending
        </div>
      </div>
      {m && (
        <p className="chart-readout">
          <strong>{monthYear(m.month)}</strong> Income {dollars(m.income)} · Spending{' '}
          {dollars(m.spending)} ·{' '}
          <span className={cents(m.profit) < 0 ? 'error' : undefined}>
            {cents(m.profit) < 0 ? 'Loss' : 'Profit'} {dollars(m.profit.replace('-', ''))}
          </span>
        </p>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Income and spending by month. ${summary}`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} />
            <text className="axis" x={PAD.left - 8} y={y(t) + 4} textAnchor="end">
              {short(t)}
            </text>
          </g>
        ))}
        {months.map((x, i) => {
          const cx = PAD.left + slot * i + slot / 2;
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: the figures are also in the label and the readout
            <g
              key={x.month}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={() => setHover(i)}
              className={i === focus ? 'month on' : 'month'}
            >
              <rect className="hit" x={cx - slot / 2} y={PAD.top} width={slot} height={innerH} />
              <rect
                className="bar in"
                x={cx - bar - 1}
                y={y(cents(x.income))}
                width={bar}
                height={PAD.top + innerH - y(cents(x.income))}
                rx={3}
              />
              <rect
                className="bar out"
                x={cx + 1}
                y={y(cents(x.spending))}
                width={bar}
                height={PAD.top + innerH - y(cents(x.spending))}
                rx={3}
              />
              <text className="axis" x={cx} y={H - 8} textAnchor="middle">
                {monthName(x.month)}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

/** Cash in the bank at each month end, as a line with a soft fill. */
export function CashChart({ months }: { months: TrendMonth[] }) {
  const values = months.map((m) => cents(m.cash));
  const lo = Math.min(0, ...values);
  const cashTicks = niceTicks(Math.max(...values, 1), 2);
  const hi = cashTicks.at(-1) ?? 1;
  const w = 360;
  const h = 180;
  const pad = { top: 16, right: 12, bottom: 24, left: 48 };
  const x = (i: number) =>
    pad.left + (i * (w - pad.left - pad.right)) / Math.max(months.length - 1, 1);
  const y = (c: number) => pad.top + ((hi - c) / (hi - lo || 1)) * (h - pad.top - pad.bottom);
  const line = values
    .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(' ');
  const area = `${line} L${x(values.length - 1)} ${y(lo)} L${x(0)} ${y(lo)} Z`;
  const last = months.at(-1);
  const first = months[0];
  return (
    <figure className="chart">
      <div className="chart-head">
        <figcaption>Cash in the bank</figcaption>
        {last && <strong className="chart-figure">{dollars(last.cash)}</strong>}
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={`Cash in the bank from ${first ? monthYear(first.month) : ''} to ${last ? monthYear(last.month) : ''}: ${months.map((m) => dollars(m.cash)).join(', ')}`}
      >
        {(lo < 0 ? [lo, ...cashTicks] : cashTicks).map((t) => (
          <g key={t}>
            <line className="grid" x1={pad.left} x2={w - pad.right} y1={y(t)} y2={y(t)} />
            <text className="axis" x={pad.left - 6} y={y(t) + 4} textAnchor="end">
              {short(t)}
            </text>
          </g>
        ))}
        <path className="area" d={area} />
        <path className="line" d={line} />
        {values.length > 0 && (
          <circle className="dot" cx={x(values.length - 1)} cy={y(values.at(-1) ?? 0)} r={4} />
        )}
        {first && (
          <text className="axis" x={x(0)} y={h - 6} textAnchor="start">
            {monthName(first.month)}
          </text>
        )}
        {last && (
          <text className="axis" x={x(values.length - 1)} y={h - 6} textAnchor="end">
            {monthName(last.month)}
          </text>
        )}
      </svg>
    </figure>
  );
}

/** The biggest spending categories, as bars against the total. */
export function TopSpending(props: {
  from: string;
  total: string;
  top: { name: string; amount: string }[];
}) {
  const total = Math.max(cents(props.total), 1);
  return (
    <figure className="chart">
      <div className="chart-head">
        <figcaption>Biggest spending since {monthYear(props.from)}</figcaption>
        <strong className="chart-figure">{dollars(props.total)}</strong>
      </div>
      {props.top.length === 0 ? (
        <p className="muted">No spending recorded yet this financial year.</p>
      ) : (
        <ul className="hbars">
          {props.top.map((t) => (
            <li key={t.name}>
              <div className="hbar-label">
                <span>{t.name}</span>
                <span className="num">{dollars(t.amount)}</span>
              </div>
              <progress
                value={cents(t.amount)}
                max={total}
                aria-label={`${t.name}: ${dollars(t.amount)}`}
              />
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}

const PRODUCT_COLOURS = ['#1F5F4A', '#E8AE38', '#9BD3B8', '#7A5300', '#4F8F76', '#F3D48C'];

/** "$29.99" for AUD, "USD 159.92" for the others. */
function inCurrency(currency: string, decimal: string): string {
  const d = dollars(decimal);
  return currency === 'AUD' ? d : `${currency} ${d.replace('$', '')}`;
}

/**
 * Monthly recurring revenue per currency (currencies are never added together: that would need an
 * exchange rate). Each currency: its total, a bar split by Stripe account, and the trend so far.
 */
export function RecurringRevenue(props: {
  accounts: { name: string; mrr: Record<string, string> | null }[];
  history: { month: string; mrr: Record<string, string> }[];
}) {
  const names = props.accounts.map((a) => a.name);
  const colour = (name: string) =>
    PRODUCT_COLOURS[names.indexOf(name) % PRODUCT_COLOURS.length] ?? '#1F5F4A';
  const currencies = [...new Set(props.accounts.flatMap((a) => Object.keys(a.mrr ?? {})))].sort(
    (a, b) => (a === 'AUD' ? -1 : b === 'AUD' ? 1 : a.localeCompare(b)),
  );
  if (currencies.length === 0)
    return (
      <figure className="chart">
        <figcaption>Monthly recurring revenue</figcaption>
        <p className="muted">No active subscriptions found in Stripe yet.</p>
      </figure>
    );
  return (
    <figure className="chart">
      <div className="chart-head">
        <figcaption>Monthly recurring revenue</figcaption>
        <div className="legend">
          {props.accounts
            .filter((a) => Object.keys(a.mrr ?? {}).length > 0)
            .map((a) => (
              <span key={a.name} className="legend-item">
                <svg className="key" viewBox="0 0 10 10" aria-hidden="true">
                  <rect width="10" height="10" rx="3" fill={colour(a.name)} />
                </svg>
                {a.name}
              </span>
            ))}
        </div>
      </div>
      <div className="mrr-grid">
        {currencies.map((cur) => {
          const parts = props.accounts
            .map((a) => ({ name: a.name, value: a.mrr?.[cur] ?? '0.00' }))
            .filter((p) => cents(p.value) > 0);
          const total = parts.reduce((s, p) => s + cents(p.value), 0);
          const points = props.history
            .map((h) => ({ month: h.month, c: cents(h.mrr[cur] ?? '0') }))
            .filter((p) => p.c > 0);
          let x = 0;
          return (
            <div key={cur} className="mrr-currency">
              <span className="label">{cur}</span>
              <strong className="chart-figure">{inCurrency(cur, (total / 100).toFixed(2))}</strong>
              <svg
                className="mrr-bar"
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
                role="img"
                aria-label={`${cur}: ${parts.map((p) => `${p.name} ${inCurrency(cur, p.value)}`).join(', ')}`}
              >
                {parts.map((p) => {
                  const w = (cents(p.value) / Math.max(total, 1)) * 100;
                  const rect = (
                    <rect
                      key={p.name}
                      x={x}
                      y={0}
                      width={Math.max(w - 0.6, 0.5)}
                      height={10}
                      fill={colour(p.name)}
                    >
                      <title>{`${p.name}: ${inCurrency(cur, p.value)}`}</title>
                    </rect>
                  );
                  x += w;
                  return rect;
                })}
              </svg>
              {points.length >= 2 ? (
                <div className="mrr-trend">
                  <Spark
                    values={points.map((p) => p.c)}
                    label={`${cur} by month: ${points.map((p) => `${monthYear(p.month)} ${inCurrency(cur, (p.c / 100).toFixed(2))}`).join(', ')}`}
                  />
                  <Change
                    from={points[0]?.c ?? 0}
                    to={points.at(-1)?.c ?? 0}
                    since={points[0]?.month ?? ''}
                  />
                </div>
              ) : (
                <span className="muted small">The trend builds month by month from now.</span>
              )}
            </div>
          );
        })}
      </div>
    </figure>
  );
}

/** "+52% since Jul": the change over the trend, rounded to a whole percent. */
function Change({ from, to, since }: { from: number; to: number; since: string }) {
  if (from <= 0) return null;
  const pct = Math.round(((to - from) / from) * 100);
  const cls = pct > 0 ? 'change up' : pct < 0 ? 'change down' : 'change';
  return (
    <span className={cls}>
      {pct > 0 ? '+' : ''}
      {pct}% since {monthName(since)}
    </span>
  );
}

function Spark({ values, label }: { values: number[]; label: string }) {
  const w = 160;
  const h = 36;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const x = (i: number) => (i * w) / Math.max(values.length - 1, 1);
  const y = (v: number) => h - 4 - ((v - lo) / (hi - lo || 1)) * (h - 8);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
      <path className="line" d={d} />
      <circle className="dot" cx={x(values.length - 1)} cy={y(values.at(-1) ?? 0)} r={3} />
    </svg>
  );
}
